import React from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler
)

export default function LineChart({ 
  data, 
  title, 
  height = 300,
  color = '#3b82f6',
  showArea = false,
  loading = false,
  error = null
}) {
  if (loading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Failed to load chart data</div>
      </div>
    )
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-loading">No data available</div>
      </div>
    )
  }
  const chartData = {
    labels: data.labels,
    datasets: [
      {
        label: data.datasetLabel || 'Value',
        data: data.values,
        borderColor: color,
        backgroundColor: showArea ? color + '20' : 'transparent',
        borderWidth: 3,
        fill: showArea,
        tension: 0.4,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1500,
      easing: 'easeOutQuart',
      delay: (context) => context.dataIndex * 50
    },
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 18,
          weight: '600',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        color: '#111827',
        padding: {
          top: 16,
          bottom: 24
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        titleFont: {
          size: 13,
          weight: '600',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        bodyFont: {
          size: 12,
          weight: '400',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        padding: 12,
        callbacks: {
          title: function(context) {
            return context[0].label
          },
          label: function(context) {
            const val = Number(context.parsed.y)
            const intVal = Number.isFinite(val) ? Math.round(val) : val
            return `${data.datasetLabel || 'Value'}: ${Number(intVal).toLocaleString()}`
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(229, 231, 235, 0.5)',
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
            family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
          },
          color: '#6b7280',
          padding: 8
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(229, 231, 235, 0.5)',
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
            family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
          },
          color: '#6b7280',
          padding: 8,
          stepSize: 1,
          callback: function(value) {
            // Only render integer tick labels
            return Number.isInteger(value) ? value : ''
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      point: {
        hoverRadius: 8,
        hoverBorderWidth: 3
      }
    }
  }

  return (
    <div style={{ height: height, width: '100%' }}>
      <Line data={chartData} options={options} />
    </div>
  )
}
